// src/pages/MediaScanner/MediaScanner.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Avatar,
  Chip,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Fab,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Videocam as VideoIcon,
  Image as ImageIcon,
  CameraAlt as CameraIcon,
  CheckCircle as CheckIcon,
  Link as LinkIcon,
  Camera as CameraFabIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const MediaScanner = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [openCameraDialog, setOpenCameraDialog] = useState(false);
  const [mediaPairs, setMediaPairs] = useState([]);
  const [cameraStream, setCameraStream] = useState(null);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraCanvasRef = useRef(null);

  const steps = [
    'Upload Image',
    'Upload Video',
    'Create Image-Video Link',
    'Scan Image & Play Video'
  ];

  // Sample pairs (demo only) - sample images won't be fingerprinted; only app-created images will match robustly
  const sampleMediaPairs = [
    {
      id: 1,
      image: '/static/images/avatar/1.jpg',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      title: 'Product Demo',
      description: 'Scan this image to view product demonstration',
      imageHash: null
    },
    {
      id: 2,
      image: '/static/images/avatar/2.jpg',
      videoUrl: 'https://www.youtube.com/embed/L_jWHffIx5E',
      title: 'Training Video',
      description: 'Employee training material',
      imageHash: null
    }
  ];

  useEffect(() => {
    setMediaPairs(sampleMediaPairs);
  }, []);

  // --- Utility: compute a simple average-hash (aHash) from a dataURL ---
  const getImageHashFromDataUrl = (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous'; // try to avoid tainting if external
      img.onload = () => {
        // Draw to small canvas (8x8) for aHash
        const size = 8;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        // draw image scaled to size
        ctx.drawImage(img, 0, 0, size, size);
        const imgData = ctx.getImageData(0, 0, size, size).data;
        const grayscale = [];
        for (let i = 0; i < imgData.length; i += 4) {
          const r = imgData[i], g = imgData[i + 1], b = imgData[i + 2];
          grayscale.push((r * 0.299 + g * 0.587 + b * 0.114));
        }
        const avg = grayscale.reduce((a, b) => a + b, 0) / grayscale.length;
        let hash = '';
        for (let v of grayscale) hash += (v > avg ? '1' : '0');
        resolve(hash); // 64-bit string
      };
      img.onerror = (e) => {
        reject(new Error('Unable to load image for hashing.'));
      };
      img.src = dataUrl;
    });
  };

  // Hamming distance of two binary strings
  const hammingDistance = (a, b) => {
    if (!a || !b || a.length !== b.length) return Infinity;
    let dist = 0;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) dist++;
    return dist;
  };

  // --- Handle image upload ---
  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target.result;
      setUploadedImage(imageData);
      setActiveStep(1);
    };
    reader.readAsDataURL(file);
  };

  // --- Handle video upload ---
  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const videoBlobUrl = URL.createObjectURL(file);
    setUploadedVideo({
      file,
      url: videoBlobUrl,
      name: file.name,
      isExternal: false
    });
    setActiveStep(2);
  };

  const handleVideoUrlSubmit = () => {
    if (!videoUrl) {
      alert('Please enter a video URL');
      return;
    }
    let embedUrl = videoUrl;
    if (videoUrl.includes('youtube.com/watch?v=')) {
      const videoId = videoUrl.split('v=')[1]?.split('&')[0];
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } else if (videoUrl.includes('vimeo.com/')) {
      const videoId = videoUrl.split('vimeo.com/')[1];
      embedUrl = `https://player.vimeo.com/video/${videoId}`;
    }
    setUploadedVideo({
      url: embedUrl,
      name: 'Linked Video',
      isExternal: true
    });
    setActiveStep(2);
  };

  // --- Create image-video link (compute hash and store) ---
  const createMediaLink = async () => {
    if (!uploadedImage || !uploadedVideo) {
      alert('Please complete both image and video steps');
      return;
    }
    try {
      const imageHash = await getImageHashFromDataUrl(uploadedImage);

      const newMediaPair = {
        id: Date.now(),
        image: uploadedImage,
        video: uploadedVideo,
        title: `Media Link ${mediaPairs.length + 1}`,
        description: 'Scan this image to play the video',
        createdAt: new Date().toLocaleString(),
        imageHash
      };

      setMediaPairs(prev => [newMediaPair, ...prev]);

      // Download the scannable image for printing/display
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = uploadedImage;
        link.download = 'scannable-image.jpg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setActiveStep(3);
        alert('Image–video link created! The image has been downloaded. You can now scan it with the camera.');
      }, 400);
    } catch (err) {
      console.error('Error creating media link:', err);
      alert('Failed to create link — could not compute image fingerprint.');
    }
  };

  // --- Start camera ---
  const startCamera = async () => {
    try {
      // open dialog first so video element exists
      setOpenCameraDialog(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setCameraStream(stream);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
      setOpenCameraDialog(false);
    }
  };

  // Assign stream to video element once it's mounted
  useEffect(() => {
    if (cameraVideoRef.current && cameraStream) {
      cameraVideoRef.current.srcObject = cameraStream;
      cameraVideoRef.current.play().catch(() => { /* ignore play errors */ });
    }
  }, [cameraStream, openCameraDialog]);

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    if (cameraVideoRef.current) {
      try { cameraVideoRef.current.srcObject = null; } catch (e) { /* ignore */ }
    }
  };

  const handleCameraClose = () => {
    stopCamera();
    setOpenCameraDialog(false);
    setIsScanning(false);
  };

  // Capture and analyze image from camera
  const captureAndAnalyze = async () => {
    if (!cameraVideoRef.current || !cameraCanvasRef.current) return;
    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    const ctx = canvas.getContext('2d');

    // set canvas to video frame size (or scaled)
    const scale = 1;
    canvas.width = Math.max(64, Math.floor(video.videoWidth * scale));
    canvas.height = Math.max(64, Math.floor(video.videoHeight * scale));
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    setIsScanning(true);

    // small delay to let UI update
    setTimeout(async () => {
      try {
        // Convert captured frame to data URL
        const capturedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        // compute hash
        const capturedHash = await getImageHashFromDataUrl(capturedDataUrl);

        // find best match by hamming distance
        let best = null;
        let bestDist = Infinity;
        for (let pair of mediaPairs) {
          if (!pair.imageHash) continue; // skip pairs without computed hash (like sample images)
          const dist = hammingDistance(capturedHash, pair.imageHash);
          if (dist < bestDist) {
            bestDist = dist;
            best = pair;
          }
        }

        // threshold — for 64-bit aHash, allow e.g. <= 12 differences (tune if needed)
        const THRESHOLD = 12;
        setIsScanning(false);

        if (best && bestDist <= THRESHOLD) {
          const videoLink = best.videoUrl || best.video?.url || best.video?.file && best.video.url;
          setScanResult({
            success: true,
            videoUrl: videoLink,
            title: best.title,
            confidence: Math.max(50, Math.round(((64 - bestDist) / 64) * 100)),
            pair: best
          });
          // close camera and show video
          setOpenCameraDialog(false);
          stopCamera();
          setOpenVideoDialog(true);
        } else {
          setScanResult({
            success: false,
            message: 'No matching image found.'
          });
          alert('Image not recognized. Make sure you scan the exact image downloaded from this app.');
        }
      } catch (err) {
        console.error('Error analyzing capture:', err);
        setIsScanning(false);
        alert('Failed to analyze the captured image.');
      }
    }, 250);
  };

  // auto-capture after opening camera for demo convenience
  useEffect(() => {
    if (openCameraDialog) {
      const t = setTimeout(() => {
        if (!isScanning) captureAndAnalyze();
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [openCameraDialog]);

  const resetScanner = () => {
    setUploadedImage(null);
    setUploadedVideo(null);
    setVideoUrl('');
    setScanResult(null);
    setActiveStep(0);
    setOpenVideoDialog(false);
    stopCamera();
  };

  // Trigger file inputs
  const triggerImageUpload = () => imageInputRef.current?.click();
  const triggerVideoUpload = () => videoInputRef.current?.click();

  // Render step UI (kept mostly same as your original)
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                mb: 3,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}08`
                }
              }}
              onClick={triggerImageUpload}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={imageInputRef}
                style={{ display: 'none' }}
              />
              {uploadedImage ? (
                <Box>
                  <img
                    src={uploadedImage}
                    alt="Uploaded preview"
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, marginBottom: 16 }}
                  />
                  <Typography variant="body2" color="textSecondary">✓ Image uploaded successfully</Typography>
                </Box>
              ) : (
                <Box>
                  <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>Upload Image</Typography>
                  <Typography variant="body2" color="textSecondary">Click to upload any image file</Typography>
                </Box>
              )}
            </Paper>
            {uploadedImage && (
              <Button fullWidth variant="contained" onClick={() => setActiveStep(1)} endIcon={<CheckIcon />}>
                Continue to Video Upload
              </Button>
            )}
          </Box>
        );
      case 1:
        return (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, cursor: 'pointer' }} onClick={triggerVideoUpload}>
                  <input type="file" accept="video/*" onChange={handleVideoUpload} ref={videoInputRef} style={{ display: 'none' }} />
                  <VideoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>Upload Video File</Typography>
                  <Typography variant="body2" color="textSecondary">MP4, MOV, AVI</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom textAlign="center">Or Use Video URL</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField fullWidth placeholder="Paste YouTube or Vimeo URL" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} size="small" />
                    <Button variant="outlined" startIcon={<LinkIcon />} onClick={handleVideoUrlSubmit} disabled={!videoUrl}>Link Video URL</Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
            {(uploadedVideo || videoUrl) && (
              <Button fullWidth variant="contained" sx={{ mt: 3 }} onClick={() => setActiveStep(2)} endIcon={<CheckIcon />}>
                Continue to Create Link
              </Button>
            )}
          </Box>
        );
      case 2:
        return (
          <Box textAlign="center">
            <Paper variant="outlined" sx={{ p: 4, mb: 3 }}>
              <SearchIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Create Image-Video Link</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>Link your image with the video. When you scan the image with the camera, the video will play automatically.</Typography>
              <Box display="flex" gap={2} justifyContent="center" sx={{ mb: 3 }}>
                <Chip icon={<ImageIcon />} label="Image Ready" color="primary" />
                <Chip icon={<VideoIcon />} label="Video Ready" color="secondary" />
              </Box>
              <Button variant="contained" size="large" startIcon={<LinkIcon />} onClick={createMediaLink} sx={{ minWidth: 200 }}>
                Create Image-Video Link
              </Button>
              {mediaPairs.length > 0 && (
                <Box sx={{ mt: 3, p: 2, border: `1px solid ${theme.palette.success.main}`, borderRadius: 2, backgroundColor: `${theme.palette.success.main}08` }}>
                  <Typography variant="body2" color="success.main">✅ Link created successfully! Download and print/display the image and then scan it.</Typography>
                </Box>
              )}
            </Paper>
          </Box>
        );
      case 3:
        return (
          <Box textAlign="center">
            <Paper variant="outlined" sx={{ p: 4, mb: 3 }}>
              <CameraIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight={600}>Ready to Scan Images!</Typography>
              <Typography variant="body1" color="textSecondary" paragraph>Use your camera to scan any image that was created in this app. The associated video will play automatically.</Typography>
              <Button variant="contained" size="large" startIcon={<CameraIcon />} onClick={startCamera} sx={{ minWidth: 200, mb: 3 }}>
                Open Image Scanner
              </Button>
              <Typography variant="body2" color="textSecondary">Point your camera at any image created in this app</Typography>
            </Paper>
            <Box textAlign="left">
              <Typography variant="h6" gutterBottom>Your Scannable Images</Typography>
              <Grid container spacing={2}>
                {mediaPairs.map((pair) => (
                  <Grid item xs={12} md={6} key={pair.id}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar src={pair.image} variant="rounded" sx={{ width: 60, height: 60 }} />
                        <Box flex={1}>
                          <Typography variant="subtitle1" fontWeight={600}>{pair.title}</Typography>
                          <Typography variant="caption" color="textSecondary">{pair.description}</Typography>
                        </Box>
                        <Chip label="Scannable" size="small" color="success" variant="outlined" />
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, margin: '0 auto', pb: 8 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h3" gutterBottom fontWeight={700}>
          <SearchIcon sx={{ fontSize: 40, mr: 2, verticalAlign: 'middle' }} />
          Image Video Scanner
        </Typography>
        <Typography variant="h6" color="textSecondary">Scan regular images to automatically play associated videos</Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {renderStepContent(activeStep)}
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" sx={{ mt: 3 }}>
        <Button onClick={resetScanner} startIcon={<DeleteIcon />}>Start Over</Button>
        {activeStep > 0 && (<Button onClick={() => setActiveStep(prev => prev - 1)}>Back</Button>)}
      </Box>

      {/* Camera Dialog */}
      <Dialog open={openCameraDialog} onClose={handleCameraClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CameraIcon color="primary" /> Image Scanner
            {isScanning ? (<Chip label="Analyzing Image..." size="small" color="warning" />) : (<Chip label="Ready to Scan" size="small" color="success" />)}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative' }}>
            <video
              ref={cameraVideoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', borderRadius: 8, border: `2px solid ${theme.palette.primary.main}` }}
            />
            {isScanning && (
              <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8 }}>
                <Box textAlign="center" color="white">
                  <CircularProgress />
                  <Typography variant="h6" sx={{ mt: 2 }}>Analyzing Image...</Typography>
                </Box>
              </Box>
            )}
            <canvas ref={cameraCanvasRef} style={{ display: 'none' }} />
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
            {isScanning ? 'Processing image...' : 'Point camera at any image created in this app. Video will play automatically.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCameraClose}>Close Scanner</Button>
          {!isScanning && (<Button variant="contained" startIcon={<SearchIcon />} onClick={captureAndAnalyze}>Scan Now</Button>)}
        </DialogActions>
      </Dialog>

      {/* Video Dialog */}
      <Dialog open={openVideoDialog} onClose={() => setOpenVideoDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VideoIcon color="primary" />
            {scanResult?.title || 'Video Found!'}
            {scanResult?.confidence && <Chip label={`${scanResult.confidence}% match`} size="small" color="success" sx={{ ml: 2 }} />}
          </Box>
        </DialogTitle>
        <DialogContent>
          {scanResult?.videoUrl ? (
            <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
              <iframe
                src={scanResult.videoUrl}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; autoplay"
                allowFullScreen
                title="Scanned Video"
              />
            </Box>
          ) : (
            <Typography>No video URL available for this item.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={startCamera}>Scan Another Image</Button>
          <Button onClick={() => setOpenVideoDialog(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {activeStep === 3 && (
        <Fab color="primary" aria-label="scan" onClick={startCamera} sx={{ position: 'fixed', bottom: 24, right: 24 }}>
          <CameraFabIcon />
        </Fab>
      )}
    </Box>
  );
};

// small inline spinner
const CircularProgress = () => (
  <Box sx={{ display: 'inline-flex' }}>
    <svg width={40} height={40} viewBox="0 0 40 40">
      <circle cx={20} cy={20} r={18} stroke="currentColor" strokeWidth={3} fill="none" strokeDasharray={113} strokeDashoffset={113} style={{ animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { stroke-dashoffset: 0; } }`}</style>
    </svg>
  </Box>
);

export default MediaScanner;
